import * as React from 'react'
import styled from 'styled-components'
import type { ColorsType } from '@components/shared/colors'
import { colors } from '@components/shared/colors'
import { Col, config, Container, Row } from 'react-awesome-styled-grid'
import TopNav from '@components/TopNav'
import { CTAPrimary, GoodSansB1, GoodSansB2, GoodSansB4, QueensH2, QueensH3 } from '@components/shared'
import { Bustle, MarieClaire, TheGuardian, TheNewYorker, Vogue, Vox } from '@components/home/PressMediaSlider'
import { useDispatch } from 'react-redux'
import { openSubscribe } from '../../actions/navigationActions'
import SubscribeFlow2 from '@components/subscribe/SubscribeFlow2'
import ThemeContext from '@components/shared/ThemeContext'
import { usePathname, useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'

export type PropsType = {
  color: ColorsType,
  inlineSubscribe: boolean,
  copyText?: {
    header?: string,
    subHeader?: string,
    subBody?: string,
    benefitsArr?: string[],
    ctaText?: string,
    trialLanguage?: string
  },
  chapterImages?: string[],
  saleEnd?: string,
  quotes?: React.ReactNode[]
}

const copy = {
  header: 'Spice up your me-time',
  subHeader: 'Prioritize your pleasure with unlimited spicy audiobooks made by women, for women',
  subBody: 'Cancel in first month, no questions asked',
  benefitsArr: [
    'Replace doom-scrolling with me-time',
    'Bite-sized romance gets you right to the good part',
    'Made by women for the female gaze'
  ],
  ctaText: 'TRY DIPSEA',
  trialLanguage: 'ONE WEEK FREE'
}

const quotes = [
  <span key={'quote-1'}>“<b>My secret weapon</b>, it solves all my needs without the need for a man in my life”</span>,
  <span key={'quote-2'}>“The app is very fun <b>especially when combined with some toys</b>. I would definitely recommend to women.”</span>,
  <span key={'quote-3'}>“I have been using this app for over a year now and the stories are amazing. <b>Perfect for exploring new things.</b>”</span>,
  <span key={'quote-4'}>“Dipsea may be the longest subscription I’ve ever maintained. And its’ for one reason: <b>there’s truly something on here for everyone</b>, and when it hits, it hits.”</span>,
  <span key={'quote-5'}>“Love this app. I can always rely on my faves to turn a boring evening into something spicy and fun. <b>I. Am. Hooked.</b>”</span>]

const chapterImages = [
  'https://api.dipseastories.com/images/500-709_NightSchoolIV_SarahAliceRabbit_Compressed.png',
  'https://api.dipseastories.com/images/500-614_GIWJay_SuzanneDias-min.jpg',
  'https://api.dipseastories.com/images/500-AI_23_WingsofWinter2_Midjourney-vEars.png',
  'https://api.dipseastories.com/images/500-736_Homecoming3_Midjourney.png',
  'https://api.dipseastories.com/images/500-891_GIWJames10_Midjourney.png'
]

function getColor (color: ColorsType, isCheckOut: boolean): { background: string, font: string, cta: string } {
  if (isCheckOut) {
    return {
      background: colors.midnightBlue,
      font: colors.white,
      cta: colors.orange
    }
  }
  switch (color) {
    case 'seaGreen':
      return {
        background: colors.seaGreen,
        font: colors.black,
        cta: colors.forestGreen
      }
    case 'pink':
      return {
        background: colors.pink,
        font: colors.black,
        cta: colors.orange
      }
    case 'white':
      return {
        background: colors.white,
        font: colors.black,
        cta: colors.orange
      }
    default:
      return {
        background: colors.midnightBlue,
        font: colors.white,
        cta: colors.orange
      }
  }
}

const CheckMark = (): React.ReactNode => <svg width="21" height="15" viewBox="0 0 21 15" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
  <path d="M1.5 7.6818L7.24694 13.5L19.1 1.5" stroke="#20614C" strokeWidth="2.5" strokeLinecap="round"
        strokeLinejoin="round"/>
</svg>

function SalesCountDown (props: { dateString: string }): React.ReactNode {
  const [countdown, setCountdown] = React.useState('')

  React.useEffect((): () => void => {
    const targetTime = new Date(props.dateString).getTime()

    const updateCountdown = (): void => {
      const now = new Date().getTime()
      const timeLeft = targetTime - now

      if (timeLeft <= 0) {
        setCountdown('NOW')
        return
      }

      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24))
      const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000)

      let formattedCountdown = `${days}D ${hours}H ${minutes}M ${seconds}S`
      if (days === 0) {
        formattedCountdown = `${hours}H ${minutes}M ${seconds}S`
      }

      if (hours === 0) {
        formattedCountdown = `${minutes}M ${seconds}S`
      }

      setCountdown(formattedCountdown)
    }

    updateCountdown() // Initial update

    const interval = setInterval(updateCountdown, 1000)

    return (): void => clearInterval(interval) // Cleanup on unmount
  }, [props.dateString])

  return <span>SALE ENDS IN {countdown}</span>
}

export default function BenefitsLandingPage (props: PropsType): React.ReactNode {
  const dispatch = useDispatch()
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const [isInlineCheckout, setIsInlineCheckout] = React.useState<boolean>(false)
  const [isTransitioning, setIsTransitioning] = React.useState<boolean>(false)
  React.useEffect((): void => {
    const active = searchParams.get('active')
    if (active && props.inlineSubscribe && !isInlineCheckout) {
      setIsInlineCheckout(true)
    } else {
      setIsInlineCheckout(false)
    }
  }, [searchParams])

  const createQueryString = React.useCallback(
    (name: string, value: string): string => {
      const params = new URLSearchParams(searchParams.toString())
      params.set(name, value)

      return params.toString()
    },
    [searchParams]
  )
  const openSubscribePage = (): void => {
    if (props.inlineSubscribe) {
      setIsTransitioning(true)
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })

      setTimeout((): void => {
        setIsInlineCheckout(true)
        setIsTransitioning(false)
      }, 500)
      router.push(pathname + '?' + createQueryString('active', 'true'))
    } else {
      dispatch(openSubscribe())
    }
  }
  let getTrialLanguage = <BadgeContainer $color={props.color}>{props.copyText?.trialLanguage || copy.trialLanguage}</BadgeContainer>
  if (props.saleEnd) {
    getTrialLanguage = <BadgeContainer $color={props.color}><SalesCountDown dateString={props.saleEnd}/></BadgeContainer>
  }
  const header: React.ReactNode = (
    <Container fluid={true}>
      <Row>
        <Col align={'center'}>
          <Header $color={props.color}>{props.copyText?.header || copy.header}</Header>
          <SubHeader $color={props.color}>{props.copyText?.subHeader || copy.subHeader}</SubHeader>
          <ImageContainer>
            {(props.chapterImages || chapterImages).map((img: string, index: number): React.ReactNode => {
              return <ChapterImage className={`chapter-image-${index}`} src={img} key={`chapter-${index}`}/>
            })}
          </ImageContainer>
          <BenefitsContainer>
            {getTrialLanguage}
            <SubscriptionHeader>Dipsea Unlimited</SubscriptionHeader>
            <SubscriptionBody
              style={{ marginBottom: '24px' }}>{props.copyText?.subBody || copy.subBody}</SubscriptionBody>
            <SubscribeCTA id="cta-benefits" backgroundColor={getColor(props.color, isInlineCheckout).cta}
                          onClick={openSubscribePage}>{props.copyText?.ctaText || copy.ctaText}</SubscribeCTA>
            <BenefitPointsContainer>
              {(props.copyText?.benefitsArr || copy.benefitsArr).map((benefit: string, index: number): React.ReactNode => {
                return <BenefitPointContainer
                  key={`benefit-${index}`}><CheckMark/><SubscriptionBodyPoints>{benefit}</SubscriptionBodyPoints></BenefitPointContainer>
              })}
            </BenefitPointsContainer>
          </BenefitsContainer>
          <FeaturedInHeader>Featured In:</FeaturedInHeader>
          <FeatureImageContainer>
            <Bustle color={colors.white}/>
            <MarieClaire color={colors.white}/>
            <TheGuardian color={colors.white}/>
            <Vox color={colors.white}/>
            <TheNewYorker color={colors.white}/>
            <Vogue color={colors.white}/>
          </FeatureImageContainer>
        </Col>
      </Row>
    </Container>
  )
  const subscribeSection: React.ReactNode = (<ThemeContext.Provider value={props.color}><SubscribeFlow2 isInLine
                                                                                                   confirmationInline={false}/></ThemeContext.Provider>)

  return (
    <>
      <BenefitsLandingPageContainer $color={props.color} $isTransitioning={isTransitioning}
                                    $isInlineCheckout={isInlineCheckout}>
        <TopNav
          backgroundColor={props.color}
          isDipseaLogoOnly={true}
          isAssetLoaded={true}
        />
        {isInlineCheckout ? subscribeSection : header}
      </BenefitsLandingPageContainer>
      <BenefitsLandingPageContainer $color={'white'}>
        <Container>
          <Row>
            <Col align={'center'}>
              <WhatWomenHeader>What women are saying...</WhatWomenHeader>
              <CommentSection>{(props.quotes || quotes).map((item: React.ReactNode, index: number): React.ReactNode => {
                return <CommentBox key={`quotes-${index}`}><CommentText>{item}<br/><span>⭐⭐⭐⭐⭐</span></CommentText></CommentBox>
              })}
          </CommentSection>
          <SubscribeCTA id="cta-benefits" backgroundColor={getColor(props.color, isInlineCheckout).cta}
                            onClick={openSubscribePage}>{props.copyText?.ctaText || copy.ctaText}</SubscribeCTA>
              <PrioritizeHeader>Prioritize Your Pleasure</PrioritizeHeader>
              <StatsBoxContainer>
                <StatsBox $color={props.color}>
                  <StatsBoxSmallText $color={props.color}>Skip right to the good part</StatsBoxSmallText>
                  <StatsBoxBigText $color={props.color}>
                    8
                    <StatsBoxSmallText $color={props.color}><b>minutes a day</b></StatsBoxSmallText>
                  </StatsBoxBigText>
                  <StatsBoxSmallText $color={props.color}>bite-sized romance that fits into your busy
                    schedule</StatsBoxSmallText>
                </StatsBox>
                <StatsBox $color={props.color}>
                  <StatsBoxSmallText $color={props.color}>Women love Dipsea</StatsBoxSmallText>
                  <StatsBoxBigText $color={props.color}>
                    4.7
                    <FiveStars color={getColor(props.color, isInlineCheckout).font}/>
                  </StatsBoxBigText>
                  <StatsBoxSmallText $color={props.color}>8,500+ reviews from women
                    on the Appstore & Playstore</StatsBoxSmallText>
                </StatsBox>
                <StatsBox $color={props.color}>
                  <StatsBoxSmallText $color={props.color}>Positive effect on wellbeing</StatsBoxSmallText>
                  <StatsBoxBigText $color={props.color}>
                    91%
                    <StatsBoxSmallText $color={props.color}><b>of Dipsea listeners</b></StatsBoxSmallText>
                  </StatsBoxBigText>
                  <StatsBoxSmallText $color={props.color}>Based on surveys of Dipsea weekly
                    listeners</StatsBoxSmallText>
                </StatsBox>
              </StatsBoxContainer>
              <SubscribeCTA id="cta-benefits" $marginTop={'36px'} $marginBottom={'90px'}
                            backgroundColor={getColor(props.color, isInlineCheckout).cta}
                            onClick={openSubscribePage}>{props.copyText?.ctaText || copy.ctaText}</SubscribeCTA>
            </Col>
          </Row>
        </Container>
      </BenefitsLandingPageContainer>
    </>
  )
}

const FiveStars = (props: { color: string }): React.ReactNode => <svg width="129" height="32" viewBox="0 0 129 32"
                                                                 fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clipPath="url(#clip0_77_364)">
    <g clipPath="url(#clip1_77_364)">
      <path
        d="M4.8739 29.9075C4.66836 29.7549 4.54238 29.5494 4.49597 29.2908C4.45619 29.0322 4.49929 28.7239 4.62527 28.3659L6.70387 22.1798L1.39297 18.3607C1.08134 18.1419 0.865855 17.9165 0.746507 17.6844C0.627164 17.4523 0.607273 17.2136 0.686839 16.9683C0.766401 16.7296 0.922219 16.5506 1.15427 16.4313C1.38634 16.3119 1.69465 16.2555 2.07921 16.2622L8.59352 16.302L10.5727 10.086C10.692 9.72134 10.8413 9.44618 11.0203 9.26053C11.2058 9.07488 11.4247 8.98206 11.6766 8.98206C11.9353 8.98206 12.154 9.07488 12.333 9.26053C12.5187 9.44618 12.6711 9.72134 12.7905 10.086L14.7696 16.302L21.284 16.2622C21.6685 16.2555 21.9769 16.3119 22.209 16.4313C22.441 16.5506 22.5968 16.7296 22.6763 16.9683C22.756 17.2136 22.7361 17.4523 22.6167 17.6844C22.4973 17.9165 22.2819 18.1419 21.9702 18.3607L16.6594 22.1798L18.738 28.3659C18.8639 28.7239 18.9038 29.0322 18.8572 29.2908C18.8175 29.5494 18.6949 29.7549 18.4893 29.9075C18.2838 30.0666 18.0517 30.1196 17.7931 30.0666C17.5345 30.0202 17.2528 29.8876 16.9477 29.6687L11.6766 25.7999L6.41546 29.6687C6.11046 29.8876 5.82867 30.0202 5.57009 30.0666C5.3115 30.1196 5.07944 30.0666 4.8739 29.9075ZM31.0307 29.9075C30.8251 29.7549 30.6991 29.5494 30.6527 29.2908C30.6129 29.0322 30.656 28.7239 30.7819 28.3659L32.8605 22.1798L27.5497 18.3607C27.2381 18.1419 27.0226 17.9165 26.9032 17.6844C26.7838 17.4523 26.764 17.2136 26.8436 16.9683C26.9231 16.7296 27.079 16.5506 27.3109 16.4313C27.543 16.3119 27.8514 16.2555 28.2359 16.2622L34.7503 16.302L36.7294 10.086C36.8488 9.72134 36.9979 9.44618 37.1769 9.26053C37.3626 9.07488 37.5814 8.98206 37.8334 8.98206C38.0919 8.98206 38.3108 9.07488 38.4898 9.26053C38.6754 9.44618 38.8279 9.72134 38.9473 10.086L40.9264 16.302L47.4407 16.2622C47.8253 16.2555 48.1336 16.3119 48.3657 16.4313C48.5977 16.5506 48.7536 16.7296 48.8331 16.9683C48.9127 17.2136 48.8927 17.4523 48.7734 17.6844C48.6541 17.9165 48.4385 18.1419 48.127 18.3607L42.8161 22.1798L44.8946 28.3659C45.0206 28.7239 45.0604 29.0322 45.014 29.2908C44.9742 29.5494 44.8515 29.7549 44.646 29.9075C44.4404 30.0666 44.2085 30.1196 43.9498 30.0666C43.6912 30.0202 43.4095 29.8876 43.1045 29.6687L37.8334 25.7999L32.5722 29.6687C32.2671 29.8876 31.9854 30.0202 31.7268 30.0666C31.4682 30.1196 31.2362 30.0666 31.0307 29.9075ZM57.1873 29.9075C56.9818 29.7549 56.8558 29.5494 56.8094 29.2908C56.7697 29.0322 56.8127 28.7239 56.9387 28.3659L59.0173 22.1798L53.7064 18.3607C53.3947 18.1419 53.1793 17.9165 53.0599 17.6844C52.9406 17.4523 52.9207 17.2136 53.0003 16.9683C53.0798 16.7296 53.2356 16.5506 53.4677 16.4313C53.6998 16.3119 54.008 16.2555 54.3927 16.2622L60.9069 16.302L62.8861 10.086C63.0055 9.72134 63.1546 9.44618 63.3336 9.26053C63.5193 9.07488 63.7382 8.98206 63.99 8.98206C64.2486 8.98206 64.4675 9.07488 64.6465 9.26053C64.8322 9.44618 64.9846 9.72134 65.104 10.086L67.0831 16.302L73.5974 16.2622C73.982 16.2555 74.2904 16.3119 74.5223 16.4313C74.7544 16.5506 74.9102 16.7296 74.9898 16.9683C75.0693 17.2136 75.0494 17.4523 74.9302 17.6844C74.8108 17.9165 74.5953 18.1419 74.2837 18.3607L68.9727 22.1798L71.0513 28.3659C71.1774 28.7239 71.2171 29.0322 71.1707 29.2908C71.131 29.5494 71.0083 29.7549 70.8027 29.9075C70.5972 30.0666 70.3651 30.1196 70.1066 30.0666C69.848 30.0202 69.5661 29.8876 69.2612 29.6687L63.99 25.7999L58.7289 29.6687C58.4239 29.8876 58.142 30.0202 57.8835 30.0666C57.6249 30.1196 57.3928 30.0666 57.1873 29.9075ZM83.344 29.9075C83.1385 29.7549 83.0126 29.5494 82.9661 29.2908C82.9263 29.0322 82.9695 28.7239 83.0954 28.3659L85.174 22.1798L79.8632 18.3607C79.5515 18.1419 79.336 17.9165 79.2167 17.6844C79.0973 17.4523 79.0774 17.2136 79.1569 16.9683C79.2365 16.7296 79.3923 16.5506 79.6244 16.4313C79.8565 16.3119 80.1648 16.2555 80.5493 16.2622L87.0636 16.302L89.0429 10.086C89.1621 9.72134 89.3114 9.44618 89.4904 9.26053C89.676 9.07488 89.8948 8.98206 90.1467 8.98206C90.4054 8.98206 90.6241 9.07488 90.8032 9.26053C90.9888 9.44618 91.1414 9.72134 91.2606 10.086L93.2398 16.302L99.7542 16.2622C100.139 16.2555 100.447 16.3119 100.679 16.4313C100.911 16.5506 101.067 16.7296 101.146 16.9683C101.226 17.2136 101.206 17.4523 101.087 17.6844C100.967 17.9165 100.752 18.1419 100.44 18.3607L95.1295 22.1798L97.2081 28.3659C97.3341 28.7239 97.3739 29.0322 97.3274 29.2908C97.2877 29.5494 97.165 29.7549 96.9595 29.9075C96.7539 30.0666 96.5218 30.1196 96.2633 30.0666C96.0047 30.0202 95.7229 29.8876 95.4178 29.6687L90.1467 25.7999L84.8856 29.6687C84.5806 29.8876 84.2988 30.0202 84.0403 30.0666C83.7816 30.1196 83.5496 30.0666 83.344 29.9075ZM109.501 29.9075C109.295 29.7549 109.169 29.5494 109.123 29.2908C109.083 29.0322 109.126 28.7239 109.252 28.3659L111.331 22.1798L106.02 18.3607C105.708 18.1419 105.493 17.9165 105.373 17.6844C105.254 17.4523 105.234 17.2136 105.314 16.9683C105.393 16.7296 105.549 16.5506 105.781 16.4313C106.013 16.3119 106.322 16.2555 106.706 16.2622L113.22 16.302L115.199 10.086C115.319 9.72134 115.468 9.44618 115.648 9.26053C115.833 9.07488 116.052 8.98206 116.304 8.98206C116.562 8.98206 116.781 9.07488 116.96 9.26053C117.146 9.44618 117.299 9.72134 117.417 10.086L119.396 16.302L125.911 16.2622C126.296 16.2555 126.603 16.3119 126.835 16.4313C127.067 16.5506 127.224 16.7296 127.303 16.9683C127.383 17.2136 127.363 17.4523 127.244 17.6844C127.124 17.9165 126.909 18.1419 126.597 18.3607L121.286 22.1798L123.365 28.3659C123.491 28.7239 123.53 29.0322 123.484 29.2908C123.444 29.5494 123.322 29.7549 123.116 29.9075C122.911 30.0666 122.678 30.1196 122.42 30.0666C122.161 30.0202 121.879 29.8876 121.575 29.6687L116.304 25.7999L111.042 29.6687C110.737 29.8876 110.456 30.0202 110.197 30.0666C109.938 30.1196 109.706 30.0666 109.501 29.9075Z"
        fill={props.color}/>
    </g>
  </g>
</svg>

const BenefitsLandingPageContainer = styled.div<any>`
    display: flex;
    flex-direction: column;
    padding-bottom: 60px;
    transition: height 0.5s ease;
    ${(props: any): string => `
      background-color: ${getColor(props.$color, props.$isInlineCheckout).background};
    `};

    ${(props: any): any => config(props).media.xs`
    `}
    ${(props: any): any => config(props).media.sm`
    `}
    ${(props: any): any => config(props).media.md`
    `}
    ${(props: any): any => config(props).media.lg`
    `}
    ${(props: any): any => config(props).media.xl`
    `}
`

const Header = styled.h1<any>`
    ${QueensH2};
    font-family: 'Queens Condensed Light';
    text-align: center;
    ${(props: any): string => `
      color: ${getColor(props.$color, false).font};
    `};
    margin-top: 48px;
    max-width: 500px;
`
const SubHeader = styled.div<any>`
    ${GoodSansB1};
    ${(props: any): string => `
      color: ${getColor(props.$color, false).font};
    `};
    max-width: 500px;
    text-align: center;
    margin-top: 24px;
`
const ImageContainer = styled.div<any>`
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-top: 24px;
    ${(props: any): any => config(props).media.md`
    max-width: 500px;
    margin-top: 48px;
  `}


`

const ChapterImage = styled.img<any>`
    width: 85px;
    height: auto;
    border-radius: 16px;

    &.chapter-image-1 {
        display: none;
    }

    ${(props: any): any => config(props).media.sm`
      width: 90px;
      &.chapter-image-1 {
          display: block;
      }
    `};
`
const BenefitsContainer = styled.div<any>`
    align-items: center;
    justify-content: center;
    display: flex;
    flex-direction: column;
    width: 100%;
    ${(props: any): any => config(props).media.md`
      width: 500px;
    `};
    background-color: ${colors.white};
    border-radius: 24px;
    margin-top: 24px;
`

const BadgeContainer = styled.div<any>`
    ${GoodSansB4};
    font-family: "Good Sans Bold";
    font-weight: bold;
    width: fit-content;
    color: ${colors.white};
    ${(props: any): string => `
      background-color: ${getColor(props.$color, false).background};
    `};
    border-radius: 8px;
    padding: 8px;
    margin-bottom: 24px;
    margin-top: 24px;
`
const SubscriptionHeader = styled.h2<any>`
    color: ${colors.midnightBlue};
    font-family: "Queens Condensed Light";
    font-style: normal;
    line-height: 110%;
    margin-block: 0;
    margin: 0;
    padding: 0;
    font-size: 36px;
`

const SubscriptionBody = styled.div<any>`
    ${GoodSansB2};
    padding: 12px;
    text-align: center;
    color: ${colors.midnightBlue};
`

const SubscriptionBodyPoints = styled.div<any>`
    ${GoodSansB2};
    font-size: 14px;
    @media (min-width: 1024px) {
        font-size: 16px;
    }
    color: ${colors.midnightBlue};
`

const SubscribeCTA = styled(CTAPrimary)<any>`
    ${(props: any): string => props.$marginTop ? `margin-top: ${props.$marginTop};` : ''};
    ${(props: any): string => props.$marginBottom ? `margin-bottom: ${props.$marginBottom};` : ''};
`
const BenefitPointsContainer = styled.div<any>`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 24px;
    margin-bottom: 24px;
    padding-left: 24px;
    padding-right: 24px;
`

const BenefitPointContainer = styled.div<any>`
    display: flex;
    width: 100%;

    margin-bottom: 8px;

    svg {
        margin-right: 8px;
    }
`

const FeaturedInHeader = styled.h3<any>`
    ${QueensH3};
    font-family: 'Queens Condensed Light';
    margin-top: 36px;
    margin-bottom: 24px;
`

const FeatureImageContainer = styled.div<any>`
    text-align: center;

    svg {
        width: 180px;
        margin-bottom: 16px;
    }

    max-width: 650px;
    ${(props: any): any => config(props).media.lg`
    svg {
        width: 180px;
    }
    `}
`

const WhatWomenHeader = styled.h2<any>`
    ${QueensH2};
    font-family: 'Queens Condensed Light';
    text-align: center;
    color: ${colors.midnightBlue};
    margin-top: 48px;
    margin-bottom: 24px;
`
const CommentSection = styled.div<any>`
    margin-bottom: 36px;
`

const CommentBox = styled.div<any>`
    border-radius: 16px;
    border: 2px solid #EDEEEF;
    background: rgba(255, 255, 255, 0.00);
    box-shadow: 0px 2px 8px 0px rgba(164, 164, 164, 0.25);
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 24px;
`

const CommentText = styled.div<any>`
    ${GoodSansB1};
    color: ${colors.midnightBlue};
    max-width: 500px;
    text-align: center;
`
const PrioritizeHeader = styled.h2<any>`
    ${QueensH2};
    font-family: 'Queens Condensed Light';
    text-align: center;
    color: ${colors.midnightBlue};
    margin-top: 48px;
    margin-bottom: 24px;
    ${(props: any): any => config(props).media.lg`
    margin-top: 48px;
    margin-bottom: 48px;
    `}
`

const StatsBoxContainer = styled.div<any>`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    ${(props: any): any => config(props).media.lg`
      flex-direction: row;
    `}
`
const StatsBox = styled.div<any>`
    padding: 24px;
    margin-top: 24px;
    ${(props: any): string => `
      background-color: ${getColor(props.$color, false).background};
    `};
    border-radius: 16px;
    width: 300px;
    ${(props: any): any => config(props).media.md`
      margin-top: 24;
      margin-right: 24px;
      width: 300px;
    `}
    ${(props: any): any => config(props).media.lg`
      margin-top: 0;
      margin-right: 24px;
      width: 280px;
    `}
`

const StatsBoxSmallText = styled.div<any>`
    ${GoodSansB1};
    max-width: 500px;
    text-align: center;
    ${(props: any): string => `
      color: ${getColor(props.$color, false).font};
    `};
`

const StatsBoxBigText = styled.div<any>`
    display: flex;
    flex-direction: column;
    align-items: center;
    color: ${colors.white};
    font-style: normal;
    line-height: 110%;
    margin-block: 0;
    margin: 0;
    padding: 0;
    text-align: center;
    font-family: "Good Sans Bold";
    font-size: 48px;
    margin-top: 24px;
    margin-bottom: 24px;
    ${(props: any): any => config(props).media.lg`
    margin-top: 36px;
    margin-bottom: 36px;
    `}
    ${(props: any): string => `
      color: ${getColor(props.$color, false).font};
    `};

    svg {
        width: 100px;
    }
`
